/**
 * Admin / owner-only market override.
 *
 * Lets the platform owner test India / Canada / UK country detection
 * from any physical location without a VPN. Sets a session-scoped
 * override that the frontend forwards as `?cc=IN` on every
 * /api/compliance/me and /api/geo/country call.
 *
 * Activation:
 *   1. Visit any page with `?test=IN`, `?test=CA`, `?test=UK` (or
 *      `?test_country=IN`). The override is captured into sessionStorage
 *      and persists across navigations within the same tab.
 *   2. Clear with `?test=clear` or by closing the tab.
 *
 * Security: the override is purely cosmetic — it flips which legal copy
 * is rendered. It does NOT bypass payments, auth, or any role check.
 */
const KEY = 'shopperden_test_country';
const VALID = new Set(['UK', 'IN', 'CA']);

export const captureMarketOverrideFromURL = () => {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('test') || params.get('test_country') || '').toUpperCase();
    if (raw === 'CLEAR') {
      sessionStorage.removeItem(KEY);
      return null;
    }
    const cc = raw === 'GB' ? 'UK' : raw;
    if (VALID.has(cc)) {
      sessionStorage.setItem(KEY, cc);
      return cc;
    }
  } catch (_e) { /* sessionStorage may be unavailable */ }
  return getMarketOverride();
};

export const getMarketOverride = () => {
  if (typeof window === 'undefined') return null;
  try {
    const cc = sessionStorage.getItem(KEY);
    return VALID.has(cc) ? cc : null;
  } catch (_e) {
    return null;
  }
};

export const clearMarketOverride = () => {
  if (typeof window === 'undefined') return;
  try { sessionStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
};

/**
 * Build a query-string fragment to append to detection endpoints.
 * Includes the override (if active) and the browser timezone as a
 * persistent fallback signal.
 */
export const buildDetectionQuery = (extras = {}) => {
  const parts = [];
  const override = getMarketOverride();
  if (override) parts.push(`cc=${encodeURIComponent(override)}`);
  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_e) { /* ignore */ }
  if (tz) parts.push(`tz=${encodeURIComponent(tz)}`);
  for (const [k, v] of Object.entries(extras)) {
    if (v !== undefined && v !== null && v !== '') parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
};
